import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../../shared/lib/supabase';
import { ReportAccess } from '../components/Pricing/ReportAccess';
import { SubscriptionUpsell } from '../components/Pricing/SubscriptionUpsell';

interface SessionData {
  session_id: string;
  status: string;
  payment_status: string;
  session_type: string;
  has_report: boolean;
  report_access_token: string | null;
  report_access_url: string | null;
  has_subscription: boolean;
  subscription_tier: string | null;
  subscription_status: string | null;
  amount_total: number;
  currency: string;
}

export default function ThankYouPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }

    fetchSessionData(sessionId);
  }, [searchParams]);

  const fetchSessionData = async (sessionId: string) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        navigate('/login');
        return;
      }

      const apiUrl = `${
        import.meta.env.VITE_SUPABASE_URL
      }/functions/v1/stripe-get-checkout-session?session_id=${sessionId}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to retrieve session data');
      }

      const data = await response.json();
      setSessionData(data);
    } catch (err: any) {
      console.error('Error fetching session data:', err);
      setError(err.message || 'Failed to load purchase details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-teal-400 mx-auto mb-4" size={48} />
          <p className="text-white text-lg">Loading your purchase details...</p>
        </div>
      </div>
    );
  }

  if (error || !sessionData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-red-900/20 border border-red-500/30 rounded-lg p-8 text-center">
          <AlertCircle className="text-red-400 mx-auto mb-4" size={48} />
          <h2 className="text-2xl font-bold text-white mb-2">Something went wrong</h2>
          <p className="text-gray-300 mb-6">{error || 'Unable to load purchase details'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2 rounded-lg font-medium"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const showUpsell =
    sessionData.session_type === 'report_only' && !sessionData.has_subscription;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500 rounded-full mb-4">
            <CheckCircle className="text-white" size={32} />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            Thank You for Your Purchase!
          </h1>
          <p className="text-xl text-gray-300">
            Your order has been successfully processed
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Summary</h2>

          <div className="space-y-3 mb-6">
            <div className="flex justify-between py-2">
              <span className="text-gray-700">Order ID</span>
              <span className="font-mono text-sm text-gray-500">
                {sessionData.session_id.slice(0, 20)}...
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-700">Payment Status</span>
              <span className="font-medium text-teal-600 capitalize">
                {sessionData.payment_status}
              </span>
            </div>
            <div className="flex justify-between py-2 text-lg font-bold border-t border-gray-200 pt-4">
              <span className="text-gray-900">Total Paid</span>
              <span className="text-gray-900">
                ${(sessionData.amount_total / 100).toFixed(2)}
              </span>
            </div>
          </div>

          {sessionData.has_subscription && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-bold text-blue-900 mb-2">Subscription Active</h3>
              <p className="text-sm text-blue-700">
                Your {sessionData.subscription_tier} plan is now active and ready to use.
              </p>
            </div>
          )}
        </div>

        {sessionData.has_report && (
          <ReportAccess
            accessToken={sessionData.report_access_token!}
            accessUrl={sessionData.report_access_url!}
          />
        )}

        {showUpsell && <SubscriptionUpsell />}

        <div className="text-center mt-12">
          <button
            onClick={() => {
              // Navigate with success parameter to trigger status refresh
              const param = sessionData.has_subscription ? 'subscription' : 'report';
              navigate(`/dashboard?${param}=success`);
            }}
            className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-lg font-bold text-lg shadow-lg transition-all"
          >
            Go to Dashboard
          </button>
        </div>

        <div className="mt-8 text-center">
          <p className="text-gray-400 text-sm mb-2">
            A confirmation email has been sent to your email address.
          </p>
          <p className="text-gray-400 text-sm">
            Questions? Contact us at{' '}
            <a
              href="mailto:support@futurelenspro.com"
              className="text-teal-400 hover:text-teal-300"
            >
              support@futurelenspro.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
